
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Company, User, UserRole } from '../types';
import { api } from '../services/apiService';

interface EditCompanyModalProps {
  company: Company;
  onClose: () => void;
  onSubmit: (data: Partial<Company>) => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ company, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: company.name,
    plan: company.plan,
    userLimit: company.userLimit
  });

  // Récupère les COMPANY_ADMIN de l'entreprise pour les afficher en lecture
  // dans la modale (utile au SUPER_ADMIN pour identifier le contact admin).
  const [admins, setAdmins] = useState<User[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoadingAdmins(true);
    api.getCompanyUsers(company.id)
      .then(list => {
        if (cancelled) return;
        setAdmins((list || []).filter(u => u.role === UserRole.COMPANY_ADMIN));
      })
      .catch(() => { if (!cancelled) setAdmins([]); })
      .finally(() => { if (!cancelled) setLoadingAdmins(false); });
    return () => { cancelled = true; };
  }, [company.id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Fire and Forget : on met à jour instantanément
    onSubmit(formData);
    onClose();
  };

  const inputCls = "w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500";
  const labelCls = "block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1";

  const modalContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-slate-900 w-full max-w-lg rounded-[3rem] shadow-2xl p-10 border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-black mb-2 text-slate-900 dark:text-white tracking-tight">Modifier Entité</h3>
        <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm">Ajustez les paramètres de l'abonnement.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
                <label className={labelCls}>Nom Société</label>
                <input required title="Nom de la société" type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Plan</label>
                    <select title="Plan d'abonnement" value={formData.plan} onChange={e => setFormData({...formData, plan: e.target.value as any})} className={inputCls}>
                        <option value="BASIC">BASIC</option>
                        <option value="PRO">PRO</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Licences Max</label>
                    <input title="Licences max" type="number" value={formData.userLimit} onChange={e => setFormData({...formData, userLimit: parseInt(e.target.value)})} className={inputCls} />
                </div>
            </div>
          </div>

          {/* Bloc admins compagnie — lecture seule, infos de contact */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/40 p-5">
            <h4 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3">
              Administrateur{admins.length > 1 ? 's' : ''} compagnie
            </h4>
            {loadingAdmins ? (
              <p className="text-xs text-slate-400 italic">Chargement…</p>
            ) : admins.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400 italic">Aucun administrateur défini pour cette entreprise.</p>
            ) : (
              <ul className="space-y-2">
                {admins.map(admin => (
                  <li key={admin.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/40">
                    <div className="w-9 h-9 rounded-full bg-indigo-500/15 dark:bg-indigo-500/25 text-indigo-600 dark:text-indigo-300 flex items-center justify-center text-sm font-black flex-shrink-0">
                      {(admin.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{admin.name}</div>
                      <a
                        href={`mailto:${admin.email}`}
                        onClick={e => e.stopPropagation()}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline truncate block"
                      >
                        {admin.email}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-4 text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest hover:text-slate-700 dark:hover:text-slate-200 transition-colors">Annuler</button>
            <button type="submit" className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black shadow-xl shadow-indigo-600/30 uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
               Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditCompanyModal;
